import { css, cx } from '@repo/pandacss/styled-system/css';
import { stack } from '@repo/pandacss/styled-system/patterns';
import { type CSSProperties } from 'react';
import { Skeleton } from '../../Skeleton';

type SkeletonRowProps = {
  driversLength?: number;
  showMobileLayout?: boolean;
};

const tableCellBaseStyles = css({
  borderTopColor: 'neutral.lighter',
  borderTopStyle: 'none',
  borderRightColor: 'neutral.lighter',
  borderRightStyle: 'none',
  borderLeftStyle: 'none',
  borderLeftColor: 'neutral.lighter',
  borderBottomWidth: 'thin',
  borderBottomStyle: 'solid',
  borderBottomColor: 'neutral.lighter',
  verticalAlign: 'top',
});

export const SkeletonRow = ({ driversLength = 0, showMobileLayout = false }: SkeletonRowProps) => {
  const mobileCellDisplay = showMobileLayout
    ? css({ display: { base: 'table-cell', sm: 'none' } })
    : undefined;
  const desktopCellDisplay = showMobileLayout
    ? css({ display: { base: 'none', sm: 'table-cell' } })
    : undefined;

  return (
    <tr data-testid="skeleton-row">
      {showMobileLayout && (
        <>
          {/* Mobile ProductCell skeleton */}
          <td
            className={cx(
              css({
                bgColor: 'neutral.lightest',
                paddingTop: '0',
                paddingRight: '0',
                paddingBottom: '0',
                paddingLeft: '0',
                borderRightWidth: 'thin',
                borderRightStyle: 'solid',
                borderRightColor: 'neutral.lighter2',
              }),
              mobileCellDisplay,
            )}
          >
            <div
              className={stack({
                direction: 'column',
                align: 'center',
                justify: 'center',
                paddingInlineStart: '2',
                paddingInlineEnd: '2',
                paddingBlockStart: '4',
                paddingBlockEnd: '4',
                columnGap: '2',
                rowGap: '2',
                height: 'full',
              })}
            >
              <Skeleton width={122} height={44} />
              <Skeleton width={120} height={44} />
              <Skeleton width={80} height={20} />
              <Skeleton width="100%" height={70} />
              <Skeleton width={100} height={14} />
            </div>
          </td>

          {/* Mobile DetailsCell skeleton */}
          <td
            className={cx(
              css({
                verticalAlign: 'top',
                borderTopColor: 'neutral.lighter2',
                borderRightColor: 'neutral.lighter2',
                borderBottomColor: 'neutral.lighter2',
                borderLeftColor: 'neutral.lighter2',
                paddingTop: '4',
                paddingRight: '4',
                paddingBottom: '4',
                paddingLeft: '4',
              }),
              mobileCellDisplay,
            )}
          >
            <div
              className={stack({
                direction: 'column',
                columnGap: '4',
                rowGap: '4',
                height: 'full',
              })}
            >
              {Array.from({ length: driversLength }).map((_, index) => (
                <Skeleton key={index} width="100%" height={75} />
              ))}
            </div>
          </td>
        </>
      )}
      <>
        {/* Desktop layout */}
        <td
          className={cx(
            tableCellBaseStyles,
            desktopCellDisplay,
            css({
              minWidth: { md: '[190px]' },
              width: { base: '[190px]', md: 'var(--skeleton-cell-md-width)' },
              paddingBlockStart: '4',
              paddingBlockEnd: '4',
              paddingLeft: '0',
              paddingRight: '2',
              backgroundColor: 'white',
              position: 'relative',
              height: { base: '[200px]', md: '[147px]' },
            }),
          )}
          style={
            {
              '--skeleton-cell-md-width': driversLength ? '[190px]' : 'auto',
            } as CSSProperties
          }
        >
          <Skeleton className={css({ height: 'full' })} />
        </td>
        <td
          className={cx(
            tableCellBaseStyles,
            desktopCellDisplay,
            css({
              paddingBlockStart: '4',
              paddingBlockEnd: '4',
              paddingInlineStart: '2',
              paddingInlineEnd: '2',
              width: '[230px]',
              minWidth: '[130px]',
              height: { base: '[200px]', md: '[147px]' },
            }),
          )}
        >
          <Skeleton className={css({ height: 'full' })} />
        </td>
        {Array.from({ length: driversLength }).map((_, index) => (
          <td
            key={index}
            className={cx(
              tableCellBaseStyles,
              desktopCellDisplay,
              css({
                paddingBlockStart: '4',
                paddingBlockEnd: '4',
                paddingInlineStart: '2',
                paddingInlineEnd: '2',
                width: '[190px]',
                minWidth: '[130px]',
                overflowX: 'hidden',
                overflowY: 'hidden',
                textOverflow: 'ellipsis',
                height: { base: '[200px]', md: '[147px]' },
              }),
            )}
          >
            <Skeleton className={css({ height: 'full' })} />
          </td>
        ))}
        <td
          className={cx(
            tableCellBaseStyles,
            css({
              width: '[159px]',
              paddingBlockStart: '4',
              paddingBlockEnd: '4',
              paddingInlineStart: '2',
              paddingInlineEnd: '2',
              display: { base: 'none', md: 'table-cell' },
              height: { base: '[200px]', md: '[147px]' },
            }),
          )}
        >
          <Skeleton className={css({ height: 'full' })} />
        </td>
      </>
    </tr>
  );
};
